import { ChangeDetectorRef, Component, DoCheck, ElementRef, HostListener, Injector, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { ShellLoadPhase, ThemeType } from '@suiteux/suiteux-shell';
import { combineLatest, Observable, Subscription } from 'rxjs';
import { filter, take } from 'rxjs/operators';

import { FeatureCalloutService } from './feature-callout/feature-callout.service';
import { FirstRunConfigService } from './first-run/first-run-component/first-run-config.service';
import { SearchConfigService } from './search-results/services/search-config.service';
import { OfficeRoute } from './shared/constants/office-route.enum';
import { ThemeRootCssClass } from './shared/constants/theme-shade.enum';
import { InstrumentationService, KnownInstrumentationEvents } from './shared/instrumentation/instrumentation.service';
import { EventMetric, UserProfileService } from './shared/instrumentation/user-profile.service';
import { FileType } from './shared/models/file-item.model';
import { ObservableScope } from './shared/models/observable-scope.enum';
import { AccessTokenService } from './shared/services/access-token.service';
import { AdminPolicyService } from './shared/services/admin-policy.service';
import { AuthState, CheckLoginResponse } from './shared/services/auth-state.service';
import { BrowserType, DeviceDetector } from './shared/services/device-detector.service';
import { DocumentService } from './shared/services/document.service';
import { EventService } from './shared/services/event.service';
import { OfficeHomeStorageService } from './shared/services/office-home-storage.service';
import { OfficeRouteService } from './shared/services/office-route.service';
import { OfficeTitleService } from './shared/services/office-title.service';
import { SchedulePriority, SchedulerService } from './shared/services/scheduler.service';
import { ShellShimService } from './shared/services/shell-shim.service';
import { ThemingService } from './shared/services/theming.service';
import { WebPwaService } from './shared/services/web-pwa.service';
import { WindowService } from './shared/services/window.service';
import { AngularEvents } from './shared/shared-context/angular-events';
import { CapabilitiesService } from './shared/shared-context/capabilities.service';
import { AuthType, ClientStartupConfigService } from './shared/shared-context/client-startup-config.service';
import { ConsumerInstallConfigService } from './shared/shared-context/consumer-install-config.service';
import { ContextService } from './shared/shared-context/context.service';
import { LocService } from './shared/shared-context/loc.service';
import { ReactShellConfigService } from './shared/shared-context/react-shell-config.service';
import { ConsumerUserConsentGroup, UserConsentConfigService } from './shared/shared-context/user-consent-config.service';
import { UserContextService } from './shared/shared-context/user-context.service';
import { DragAndDrop } from './shared/utilities/drag-and-drop.utility';
import { UploadButtonService } from './upload/upload-button/upload-button.service';
import { UploadConfigService } from './upload/upload-config.service';
import { UpsellConfigService } from './upsell/upsell-config.service';

@Component({
    selector: 'ohp-app',
    templateUrl: './app.component.html'
})
export class AppComponent implements OnInit, OnDestroy, DoCheck {
    @ViewChild('scrollElement') public scrollElement: ElementRef;

    public actionContextMenuEnabled: boolean;
    public appBarEnabled: boolean;
    public backgroundStyle: { [key: string]: string } = {};
    public bottomTrayEnabled: boolean;
    public createOneNotePageEnabled: boolean;
    public customPivots: boolean;
    public feedbackEnabled: boolean = false;
    public firstRunEnabled: boolean;
    public fluentHeaderEnabled: boolean;
    public fluentThemeRefreshEnabled: boolean;
    public fluidSupportEnabled: boolean;
    public hideSearch: boolean;
    public isConsumer: boolean;
    public isIE: boolean;
    public isMobile: boolean;
    public isSearchPage: boolean = false;
    public isStandalone: Observable<boolean>;
    public leftNavReact: boolean;
    public loadFeatureCalloutModule: boolean;
    public loadShareControlModuleForContentPage: boolean;
    public loadMoveToFolderModuleForContentPage: boolean;
    public memoryProfilingEnabled: boolean;
    public mobileMenusEnabled: boolean;
    public nativeSearchEnabled: boolean;
    public officeHomeClass: Array<string> = [];
    public onAppGallery: boolean;
    public onContentPage: boolean;
    public onFluidContentPage: boolean;
    public onM365AppsPage: boolean;
    public onWacContentPage: boolean;
    public onHomePage: boolean;
    public onMyContentPage: boolean;
    public pullToRefreshEnabled: boolean;
    public searchEnabled: boolean;
    public searchUXGeneralFeedbackEnabled: boolean = false;
    public showBackgroundImage: boolean;
    public showFASTBreadcrumb: boolean;
    public showMobileAppBanner: boolean;
    public showPremiumUpsellModal: boolean;
    public showWorkspaceNav: boolean;
    public stayingAware: boolean;
    public supportCentralEnabled: boolean;
    public taskModule: boolean;
    public themeRootCssClass: string;
    public toastContainer: boolean;
    public uploadEnabled: boolean;
    public viewInitializationCompleted = false;
    public viewListenersInitialized = false;
    public viewMetric: EventMetric;
    public wacHostEnabled: Observable<boolean>;
    public webVitalsEnabled: boolean;
    public showNewCommercialUserShelfExpanded: boolean;
    public showNewUserShelfCollapsed: boolean;
    public showNewConsumerExperiences: boolean;
    public newOfficeTwoExperience: boolean; //Office2.0 experience
    public newOfficeHomePageExperience: boolean; //Office2.0 experience on HomePage
    public sidePopUpEnabled: boolean;

    private appGalleryReactEnabled: boolean;
    private shellHeader: HTMLElement;
    private contentHeight: number;
    private contentWidth: number;
    private authStateChangeSubscription: Subscription;
    private routeChangeSubscription: Subscription;
    private routeChangeEndSubscription: Subscription;
    private backgroundImageSubscription: Subscription;
    private featureCalloutSubscription: Subscription;
    private dialogOpenSubscription: Subscription;
    private dialogCloseSubscription: Subscription;
    private fluentProviderClassSubscription: Subscription;
    public searchUXGeneralFeedbackSubscription: Subscription;
    private dragAndDropUtility: DragAndDrop;
    private upsellButtonUtility: import('./shared/utilities/upsell-button.utility').UpsellButton;
    private meetNowButtonUtility: import('./shared/utilities/meet-now-button.utility').MeetNowButton;

    // These services are set after the second flush is complete.
    private capabilitiesService: CapabilitiesService;
    private contextService: ContextService;
    private featureCalloutService: FeatureCalloutService;
    private instrumentationService: InstrumentationService;
    private officeRouteService: OfficeRouteService;
    private officeTitleService: OfficeTitleService;
    private themingService: ThemingService;
    private userProfileService: UserProfileService;
    private scheduler: SchedulerService;
    private searchConfigService: SearchConfigService;
    private startupConfigService: ClientStartupConfigService;
    private locService: LocService;
    private reactShellConfigService: ReactShellConfigService;
    private userStorageService: OfficeHomeStorageService;
    private adminPolicyService: AdminPolicyService;

    // IMPORTANT: AppComponent is instantiated with first chunk of page response which contains limited app configuration data.
    // This data is needed for bootstrapping app but insufficient for populating app UI. Most app configuration data arrives
    // in second chunk so AppComponent has to wait for AngularEvents.appInitialized event before completing initialization.
    // Thus do not pass app-specific services to constructor! These may trigger cascade effect of instantiating dependant services
    // which are not meant to be created before app configs are available. Instead of bringing dependency via constructor DI,
    // use this.injector.get() method to get the dependency after AngularEvents.appInitialized event is triggered.
    constructor(
        private readonly documentService: DocumentService,
        private readonly eventService: EventService,
        private readonly titleService: Title,
        private readonly windowService: WindowService,
        private readonly router: Router,
        private readonly injector: Injector,
        private readonly changeDetectorRef: ChangeDetectorRef
    ) {
    }

    public ngOnInit() {
        const initializer = () => {
            // The template is not composed until additional data comes back from the server
            // and viewInitializationCompleted is set to true. This block of code should not
            // reference anything in the template such as viewChild because they don't exist yet.
            const deviceDetector = this.injector.get(DeviceDetector);
            const uploadConfigService = this.injector.get(UploadConfigService);
            const uploadButtonService = this.injector.get(UploadButtonService);
            const accessTokenService = this.injector.get(AccessTokenService);
            this.searchConfigService = this.injector.get(SearchConfigService);
            this.startupConfigService = this.injector.get(ClientStartupConfigService);
            this.capabilitiesService = this.injector.get(CapabilitiesService);
            this.contextService = this.injector.get(ContextService);
            this.instrumentationService = this.injector.get(InstrumentationService);
            this.officeRouteService = this.injector.get(OfficeRouteService);
            this.officeTitleService = this.injector.get(OfficeTitleService);
            this.themingService = this.injector.get(ThemingService);
            this.userProfileService = this.injector.get(UserProfileService);
            this.featureCalloutService = this.injector.get(FeatureCalloutService);
            this.locService = this.injector.get(LocService);
            this.reactShellConfigService = this.injector.get(ReactShellConfigService);
            const webPwaService = this.injector.get(WebPwaService);
            const userContextService = this.injector.get(UserContextService);
            this.userStorageService = this.injector.get(OfficeHomeStorageService);
            this.isStandalone = webPwaService.isStandalone;
            this.isMobile = deviceDetector.isMobileDevice();

            const capabilities = this.capabilitiesService.capabilities;
            this.showMobileAppBanner = this.isMobile || deviceDetector.isTabletDevice();
            this.uploadEnabled = (capabilities.oneDriveUploadEnabled || capabilities.swayCreateNewEnabled) && !!uploadConfigService.config;
            this.supportCentralEnabled = capabilities.supportCentralEnabled && !this.isMobile;
            this.isConsumer = this.startupConfigService.config.authType === AuthType.MSA;
            this.bottomTrayEnabled = capabilities.bottomTrayEnabled;
            this.searchEnabled = capabilities.showSearch && !!this.searchConfigService.config && !this.bottomTrayEnabled;
            this.fluidSupportEnabled = capabilities.fluidSupportEnabled;
            this.showPremiumUpsellModal = false;
            this.nativeSearchEnabled = this.searchEnabled && capabilities.nativeSearchInHeaderEnabled;
            this.leftNavReact = this.capabilitiesService.capabilities.leftNavReact && !this.capabilitiesService.capabilities.appBarEnabled;
            const newLeftNav = this.capabilitiesService.capabilities.leftNavReact || this.capabilitiesService.capabilities.appBarEnabled;
            this.appBarEnabled = this.capabilitiesService.capabilities.appBarEnabled;
            this.appGalleryReactEnabled = this.capabilitiesService.capabilities.appGalleryReactEnabled;
            this.stayingAware = newLeftNav && this.capabilitiesService.capabilities.stayingAwareEnabled;
            this.customPivots = newLeftNav && this.capabilitiesService.capabilities.quickAccessV1CustomPivotEnabled;
            this.themeRootCssClass = ThemeRootCssClass;
            this.toastContainer = this.capabilitiesService.capabilities.contentActionsEnabled;
            this.wacHostEnabled = this.capabilitiesService.openWithUIHost;
            this.isIE = deviceDetector.deviceInfo.browser.browserType === BrowserType.IE;
            this.webVitalsEnabled = this.capabilitiesService.capabilities.webVitalsEnabled;
            this.memoryProfilingEnabled = this.capabilitiesService.capabilities.memoryProfilingEnabled;
            this.fluentThemeRefreshEnabled = this.capabilitiesService.capabilities.fluentRefresh;
            this.fluentHeaderEnabled = this.capabilitiesService.capabilities.fluentHeader;
            this.actionContextMenuEnabled = capabilities.contentActionsEnabled && this.capabilitiesService.capabilities.contentActionsOHomeEnabled;

            // New user experiences
            const newUserShelfOverride = this.capabilitiesService.capabilities.newUserShelfOverride;
            const newEmptyConsumerOverride = this.capabilitiesService.capabilities.newConsumerOverride;
            const newUserShelfEnabled = this.capabilitiesService.capabilities.newUserShelfEnabled && this.capabilitiesService.capabilities.createCenterEnabled;
            const newConsumerExperienceEnabled = this.capabilitiesService.capabilities.newConsumerExperienceEnabled;

            const isFirstUse = this.userStorageService.isFirstUse();
            const newConsumerExperiencesEnabled = this.isConsumer &&
                ((newConsumerExperienceEnabled && newUserShelfEnabled) ||
                (newEmptyConsumerOverride || newUserShelfOverride)) &&
                !this.isMobile;
            const newCommercialExperienceEnabled = !this.isConsumer &&
                (newUserShelfOverride || newUserShelfEnabled) &&
                !this.isMobile;
            if (newCommercialExperienceEnabled || newConsumerExperiencesEnabled) {
                this.showNewCommercialUserShelfExpanded = newCommercialExperienceEnabled && (isFirstUse || newUserShelfOverride);
                this.showNewConsumerExperiences = this.contextService.isNewConsumerUserExperience = (newConsumerExperiencesEnabled && (isFirstUse || newUserShelfOverride || newEmptyConsumerOverride));
                if (isFirstUse) { // if this is a new user, start tracking usage in userProfileService
                    this.userProfileService.incrementMetric(KnownInstrumentationEvents.ImpressionsSinceFirstVisit);
                } else { // if is not a new user, and we have not started tracking usage recently, don't start tracking new usage
                    this.userProfileService.doesMetricExist(KnownInstrumentationEvents.ImpressionsSinceFirstVisit).then(isRecentFirstVisit => {
                        if (isRecentFirstVisit) {
                            this.userProfileService.incrementMetric(KnownInstrumentationEvents.ImpressionsSinceFirstVisit);
                            const impressionMetric = this.userProfileService.getInitialMetric(KnownInstrumentationEvents.ImpressionsSinceFirstVisit);
                            if (impressionMetric && impressionMetric.count <= 5) {
                                this.showNewUserShelfCollapsed = newCommercialExperienceEnabled;
                                // in the consumer case we will show either the no files experience or the collapsed new user shelf
                                this.showNewConsumerExperiences = this.contextService.isNewConsumerUserExperience = (newConsumerExperiencesEnabled || newUserShelfOverride || newEmptyConsumerOverride);
                            }
                        }
                    });
                }
            }

            this.injector.get(FirstRunConfigService).getConfig().then(config => {
                this.firstRunEnabled = !userContextService.user.hasNoLicense && !!config && !!config.model;

                // Check if Feature callout module should be downloaded.
                this.featureCalloutSubscription = this.featureCalloutService.callouts.subscribe(callouts => {
                    this.loadFeatureCalloutModule = callouts.length > 0;
                });
            });

            this.scheduler = this.injector.get(SchedulerService);

            if (this.isConsumer) {
                if (this.capabilitiesService.capabilities.userConsentCallEnabled) {
                    // Set up feedback and upsell based on Age appropriate design code(AADC)
                    this.injector.get(UserConsentConfigService).getConfig().then(config => {
                        this.feedbackEnabled = config.userConsent === ConsumerUserConsentGroup.CanConsent && capabilities.showFeedback && !this.isMobile;
                        if (config.userConsent === ConsumerUserConsentGroup.CanConsent) {
                            this.prepareUpsellButton();
                        }
                    });
                } else {
                    this.feedbackEnabled = capabilities.showFeedback && !this.isMobile;
                    this.prepareUpsellButton();
                }
            } else {
                // Check admin policies if flight is enabled.
                if (this.capabilitiesService.capabilities.adminPolicyEnabled) {
                    this.adminPolicyService = this.injector.get(AdminPolicyService);
                    this.adminPolicyService.getAdminPolicy().then(adminpolicy => {
                        // Only enabled feedback when SendFeedback is enabled; Hide feedback button if failed to get policies.
                        this.feedbackEnabled = adminpolicy ? capabilities.showFeedback && !this.isMobile && adminpolicy.SendFeedback.State : false;
                    });
                } else {
                    this.feedbackEnabled = capabilities.showFeedback && !this.isMobile;
                }
            }

            this.officeHomeClass = ['angular-root'];
            this.backgroundStyle['background-image'] = `url(${this.contextService.sharedContext.fluentHeroImageUrl})`;

            this.setupNavigationHandler();

            this.viewInitializationCompleted = true;
            this.shellHeader = this.documentService.nativeDocument.getElementById('ReactShellHeader');

            this.pullToRefreshEnabled = this.capabilitiesService.capabilities.pullToRefreshEnabled;
            this.mobileMenusEnabled = this.capabilitiesService.capabilities.mobileContextMenusEnabled;

            if (this.isMobile) {
                this.instrumentationService.logGenericEvent(KnownInstrumentationEvents.MobileView, {}, [KnownInstrumentationEvents.MobileView]);
            }

            this.viewMetric = this.userProfileService.getInitialMetric(this.isMobile ? KnownInstrumentationEvents.MobileView : KnownInstrumentationEvents.HomePageView);

            const shouldUseCssBlur = !this.isIE;
            if (shouldUseCssBlur) {
                this.eventService.events.withScope(ObservableScope.OnlyFuture).pipe(
                    filter(event => event.name === AngularEvents.firstRunModalOpen),
                    take(1))
                    .toPromise()
                    .then(() => {
                        this.officeHomeClass.push('blur-background');
                        this.shellHeader.classList.add('blur-background');
                    });
                this.eventService.events.withScope(ObservableScope.OnlyFuture).pipe(
                    filter(event => event.name === AngularEvents.firstRunModalClosed || event.name === AngularEvents.premiumTemplatesUpsellClosed),
                    take(1))
                    .toPromise()
                    .then(() => {
                        const index = this.officeHomeClass.indexOf('blur-background');
                        if (index >= 0) {
                            this.officeHomeClass.splice(index, 1);
                        }
                        this.shellHeader.classList.remove('blur-background');
                    });
            }

            // Add fluent provider class to root of app to provide theme css variables
            if (this.fluentThemeRefreshEnabled) {
                this.fluentProviderClassSubscription = this.eventService.events.withScope(ObservableScope.PastAndFuture).pipe(
                    filter(event => event.name === AngularEvents.fluentClassNameChanged))
                    .subscribe(event => {
                        this.themeRootCssClass = ThemeRootCssClass + ' ' + event.args[0];
                        this.changeDetectorRef.detectChanges();
                    });
            }

            this.dragAndDropUtility = new DragAndDrop(
                this.documentService,
                uploadConfigService,
                uploadButtonService,
                accessTokenService,
                this.isConsumer);

            const shellShimService = this.injector.get(ShellShimService);
            shellShimService.getShellShim().then(shellShim => {
                if (shellShim.Header) {
                    shellShim.Header.OnShyHeaderModeChanged((hidden: boolean) => {
                        if (hidden) {
                            this.officeHomeClass.push('angular-root--hidden-shell');
                        } else {
                            const index = this.officeHomeClass.indexOf('angular-root--hidden-shell');

                            if (index >= 0) {
                                this.officeHomeClass.splice(index, 1);
                            }
                        }

                        this.changeDetectorRef.detectChanges();
                    });
                }

                if (shellShim.GetResourceLoader) {
                    const resourceLoader = shellShim.GetResourceLoader();
                    if (capabilities.nativeSearchInHeaderEnabled) {
                        resourceLoader.registerResourceCallback(phase => {
                            if (phase !== ShellLoadPhase.Complete) {
                                const priority = phase === undefined || phase === ShellLoadPhase.Plus ? SchedulePriority.VeryLow : SchedulePriority.High;

                                this.scheduler.scheduleWork(() => {
                                    resourceLoader.loadNext();
                                }, priority);
                            }
                        });
                    } else {
                        // Load all shell resources >= Shell Plus when idle.
                        this.scheduler.scheduleWork(() => {
                            resourceLoader.loadAll();
                        }, SchedulePriority.VeryLow);
                    }
                }
            });

            if (this.isConsumer) {
                this.scheduler.scheduleWork(() => {
                    import('./shared/utilities/meet-now-button.utility').then(chunk => {
                        this.meetNowButtonUtility = new chunk.MeetNowButton(this.contextService, this.eventService, this.injector);
                    });
                }, SchedulePriority.VeryLow);
            }

            // This is needed to trigger change detection logic before entire page response has been processed.
            this.changeDetectorRef.detectChanges();
        };

        // AngularEvents.appInitialized event is broadcasted when app is ready to populate UI.
        this.eventService.events.withScope(ObservableScope.PastAndFuture).pipe(
            filter(event => event.name === AngularEvents.appInitialized),
            take(1))
            .toPromise()
            .then(initializer);

        this.dialogOpenSubscription = this.eventService.events.withScope(ObservableScope.OnlyFuture).pipe(
            filter(event => event.name === AngularEvents.premiumTemplatesUpsellStarted))
            .subscribe(() => {
                const upsellConfigService = this.injector.get(UpsellConfigService);
                upsellConfigService.getConfig().then(upsellConfig => {
                    if (upsellConfig && upsellConfig.model) {
                        this.showPremiumUpsellModal = true;
                    }
                });
            });

        this.dialogCloseSubscription = this.eventService.events.withScope(ObservableScope.OnlyFuture).pipe(
            filter(event => event.name === AngularEvents.premiumTemplatesUpsellClosed))
            .subscribe(() => {
                this.showPremiumUpsellModal = false;
            });

        this.searchUXGeneralFeedbackSubscription = this.eventService.events.withScope(ObservableScope.PastAndFuture).pipe(
            filter(event => event.name === AngularEvents.searchUXGeneralFeedbackEnabled))
            .subscribe(() => {
                this.searchUXGeneralFeedbackEnabled = true;
            });
    }

    public ngDoCheck() {
        if (this.viewInitializationCompleted) {
            if (!this.viewListenersInitialized) {
                this.viewListenersInitialized = true;
                this.setAuthStateListener();
            }

            // Initial Navigation is deferred until view is loaded and <router-outlet> is available.
            if (!this.router.navigated) {
                this.router.initialNavigation();
            }
        }
    }

    public ngOnDestroy() {
        if (this.authStateChangeSubscription) {
            this.authStateChangeSubscription.unsubscribe();
        }

        if (this.routeChangeSubscription) {
            this.routeChangeSubscription.unsubscribe();
        }

        if (this.routeChangeEndSubscription) {
            this.routeChangeEndSubscription.unsubscribe();
        }

        if (this.backgroundImageSubscription) {
            this.backgroundImageSubscription.unsubscribe();
        }

        if (this.featureCalloutSubscription) {
            this.featureCalloutSubscription.unsubscribe();
        }

        if (this.upsellButtonUtility) {
            this.upsellButtonUtility.unsubscribe();
        }

        if (this.meetNowButtonUtility) {
            this.meetNowButtonUtility.unsubscribe();
        }

        if (this.dialogOpenSubscription) {
            this.dialogOpenSubscription.unsubscribe();
        }

        if (this.dialogCloseSubscription) {
            this.dialogCloseSubscription.unsubscribe();
        }

        if (this.searchUXGeneralFeedbackSubscription) {
            this.searchUXGeneralFeedbackSubscription.unsubscribe();
        }

        this.fluentProviderClassSubscription?.unsubscribe();
    }

    @HostListener('window:resize')
    public onWindowResize() {
        this.eventService.broadcastEvent({
            name: AngularEvents.windowResized,
            args: []
        });
    }

    @HostListener('window:focus')
    public onWindowFocus() {
        this.eventService.broadcastEvent({
            name: AngularEvents.windowFocused,
            args: []
        });
    }

    @HostListener('window:beforeinstallprompt', ['$event'])
    public onBeforeInstall(event) {
        this.eventService.broadcastEvent({
            name: AngularEvents.beforeInstallPrompt,
            args: [event]
        });
    }

    @HostListener('window:appinstalled')
    public onAppInstalled() {
        const event = this.isMobile ? KnownInstrumentationEvents.AppAddedToHomeScreenMobile : KnownInstrumentationEvents.AppAddedToHomeScreenDesktop;
        this.instrumentationService.logGenericEvent(event, {}, [event]);
    }

    public onResize(contentRect: DOMRectReadOnly) {
        const { width, height } = contentRect;

        if (this.contentWidth !== width) {
            this.contentWidth = width;
            this.eventService.broadcastEvent({
                name: AngularEvents.contentWidthChanged,
                args: [width]
            });
        }

        if (this.contentHeight !== height) {
            this.contentHeight = height;
            this.eventService.broadcastEvent({
                name: AngularEvents.contentHeightChanged,
                args: [height]
            });
        }
    }

    public dragoverHandler(event: DragEvent) {
        event.preventDefault();
        this.dragAndDropUtility.onDragOver(event);
    }

    public dropHandler(event: DragEvent) {
        event.preventDefault();
        this.dragAndDropUtility.onDrop(event);
    }

    public shouldShowFeedbackButton(): boolean {
        return this.feedbackEnabled
            && !this.onWacContentPage
            && !this.onM365AppsPage
            && !(this.isSearchPage && this.searchUXGeneralFeedbackEnabled);
    }

    private setAuthStateListener() {
        this.authStateChangeSubscription = this.eventService.events.withScope(ObservableScope.PastAndFuture).pipe(
            filter(event => event.name === AngularEvents.authStateChanged))
            .subscribe(event => {
                const authStateData: CheckLoginResponse = event.args[0];
                const authState = authStateData?.LoginState;

                // If in the AccountMismatch state and account switching is enabled, we need to manually trigger a MAST switchaccount dialog in the following case:
                //   1. User is logged into office.com with account 1
                //   2. User opens ODSP in a new tab and switches account to account 2
                //   3. As part of ODSP switch, account 2 gets logged into office.com before being redirected to ODSP. This is because ODSP account switch uses the
                //      www.office.com/launch/onedrive endpoint to switch accounts.
                //   4. If the user switches to office.com tab, then the MAST dialog does not get automatically triggered because MAST has not been initialized for the
                //      new account 2. So we do that here and trigger the popup.
                // If account switching is not enabled, treat AccountMismatch the same as SignedOut.
                const accountSwitchingEnabled = this.capabilitiesService.capabilities.accountSwitchingEnabled;
                if (accountSwitchingEnabled && authState === AuthState.AccountMismatch) {
                    const _window = this.windowService.nativeWindow as any;

                    // See https://office.visualstudio.com/ISS/_git/WebAuthJS?path=%2Fsrc%2Fpackages%2FMicrosoft.Office.WebAuth.Mast%2Fscripts%2FMultiAccountSessionTracker.ts&version=GBmaster&line=387&lineEnd=388&lineStartColumn=1&lineEndColumn=1&lineStyle=plain&_a=contents
                    // for MastInteraction docs.
                    _window.MastInteraction.UpdateAccount(authStateData.CurrentAccountPuid, { authenticatedState: 1, idp: authStateData.CurrentAccountType, upn: authStateData.CurrentAccountUpn }, true);
                    _window.MastInteraction.ShowPrompt('SwitchAccount');
                } else if (
                    // redirectWhenSignedOutFromWorkloads will be false if account switching is enabled. In that case, the MAST library
                    // will show a Signed Out dialog on the page, so we should not redirect to the sign out landing URL.
                    // We don't need to do anything here to trigger the MAST dialog.
                    (this.capabilitiesService.capabilities.redirectWhenSignedOutFromWorkloads && authState === AuthState.SignedOut) ||
                    (!accountSwitchingEnabled && authState === AuthState.AccountMismatch)) {

                        // Flush instrumentation logs before navigating to sign out page.
                        this.flushLogs();
                        const signOutLandingUrl = this.getSignOutLandingUrl();
                        if (signOutLandingUrl) {
                            this.windowService.nativeWindow.open(signOutLandingUrl, '_self');
                        } else {
                            this.windowService.nativeWindow.location.reload(false);
                        }
                } else if (
                    (this.capabilitiesService.capabilities.reloadWhenAuthExpired && authState === AuthState.Expired) ||
                    (this.capabilitiesService.capabilities.reloadWhenReauthRequired && authState === AuthState.ReauthenticationRequired) ||
                    (this.capabilitiesService.capabilities.reloadWhenPolicyCheckFailed && authState === AuthState.PolicyCheckFailed)) {
                    // We don't want to refresh the page on a user when they are in the middle of editing a document
                    if (!this.onWacContentPage) {
                        // Flush instrumentation logs before reloading.
                        this.flushLogs();
                        this.windowService.nativeWindow.location.reload(false);
                    }
                } else if (this.capabilitiesService.capabilities.activityBasedTimeoutEnabled && authState === AuthState.SignedOutBasedOnActivityAuto) {
                    this.flushLogs();
                    const logoutUrl =
                        this.locService.format(this.contextService.sharedContext.signOutUrlTemplate,
                            encodeURIComponent(`${this.contextService.sharedContext.abtSignoutPath}?` +
                                `rs=${this.contextService.sharedContext.officeMarket}&` +
                                `build=${this.startupConfigService.config.sharedClientStartupContext.buildId}&` +
                                `ru=${encodeURIComponent(this.windowService.nativeWindow.location.pathname + this.windowService.nativeWindow.location.search)}&` + // include query params to land back in ui host
                                `upn=${this.startupConfigService.config.userPrincipalName}`));
                    this.windowService.nativeWindow.open(logoutUrl, '_self');
                } else if (this.capabilitiesService.capabilities.activityBasedTimeoutEnabled && authState === AuthState.SignedOutBasedOnActivityUserClick) {
                    this.flushLogs();
                    this.windowService.nativeWindow.open(this.reactShellConfigService.reactShellConfig.logoutUrl, '_self');
                }
            });
    }

    private setupNavigationHandler() {
        const routeChangeStart = this.officeRouteService.getRouteChangeStartObservable();
        const routeChangeEnd = this.officeRouteService.getRouteChangeEndObservable();
        this.routeChangeSubscription = routeChangeStart.subscribe(routes => {
            const currentRoute = routes.currentRoute.route;

            // Title is set within the route so we don't want to have a race condition
            if (currentRoute !== OfficeRoute.Content) {
                const pageTitle = this.officeTitleService.getTitle(routes.currentRoute);
                let pageSubTitle: string;
                if (currentRoute === OfficeRoute.MyForms) {
                    pageSubTitle = this.locService.MyFormsHeroTitle;
                } else if (currentRoute === OfficeRoute.GroupForms) {
                    pageSubTitle = this.locService.GroupFormsTitle;
                }
                const title = pageSubTitle ? `${pageTitle} - ${pageSubTitle}` : pageTitle;
                this.titleService.setTitle(title);
            }

            // Start preloading the share control module immediately on content page navigation so that
            // the Share buttons are responsive sooner.
            this.loadShareControlModuleForContentPage = currentRoute === OfficeRoute.Content;

            if (this.capabilitiesService.capabilities.wacHostMoveToFolderEnabled) {
                this.loadMoveToFolderModuleForContentPage = currentRoute === OfficeRoute.Content;
            }

            // Do not load nativeSearchCode in directNav scenarios for Fluid content route.
            if (currentRoute === OfficeRoute.Content && routes.currentRoute.app && routes.currentRoute.app === FileType.Fluid) {
                this.nativeSearchEnabled = false;
            } else {
                this.nativeSearchEnabled = this.searchEnabled && this.capabilitiesService.capabilities.nativeSearchInHeaderEnabled;
            }

            this.documentService.updateFavicon(this.officeTitleService.getFavicon(routes.currentRoute));
            this.isSearchPage = currentRoute === OfficeRoute.Search || currentRoute === OfficeRoute.SearchSiteScoped;
            this.onM365AppsPage = currentRoute === OfficeRoute.M365Apps;
            this.onAppGallery = currentRoute === OfficeRoute.AppGallery;
            this.onHomePage = currentRoute === OfficeRoute.OfficeHome;
            this.onMyContentPage = currentRoute === OfficeRoute.MyContent;

            this.newOfficeTwoExperience = this.capabilitiesService.capabilities.quickAccessV1Enabled && this.capabilitiesService.capabilities.edgeworthEnabled;
            this.newOfficeHomePageExperience =  this.onHomePage && this.newOfficeTwoExperience;
            this.sidePopUpEnabled = currentRoute === OfficeRoute.MyForms || currentRoute === OfficeRoute.GroupForms || (currentRoute === OfficeRoute.Start && routes.currentRoute.app === FileType.Forms);
            this.createOneNotePageEnabled = this.capabilitiesService.capabilities.createOneNotePageEnabled && !(this.onHomePage && this.capabilitiesService.capabilities.createCenterEnabled);

            this.taskModule = this.capabilitiesService.capabilities.m365AppsEnabled;

            // TODO: How to handle scenarios where native search is enabled, but we chose not to use it?  i.e. Bottom Tray, etc.
            this.hideSearch = (this.searchConfigService.config && this.capabilitiesService.capabilities.nativeSearchInHeaderEnabled);
            this.showFASTBreadcrumb = currentRoute === OfficeRoute.SearchSiteScoped;
            this.showWorkspaceNav = currentRoute === OfficeRoute.AppGallery && !this.appGalleryReactEnabled;
        });

        this.routeChangeEndSubscription = routeChangeEnd.subscribe(routes => {
            const currentRoute = routes.currentRoute.route;
            this.onContentPage = currentRoute === OfficeRoute.Content;
            this.onWacContentPage = this.onContentPage && routes.currentRoute.app !== FileType.Fluid;
            this.onFluidContentPage = this.onContentPage && routes.currentRoute.app === FileType.Fluid;
        });

        this.backgroundImageSubscription = combineLatest(this.themingService.themeShades, routeChangeStart).subscribe(([themeEvent, routeEvent]) => {
            // Only show image on fluent commercial pages without app theming (Homepage, App Gallery, and Search from either)
            // Also only on the base theme and Office app theme
            const route = routeEvent.currentRoute;
            this.showBackgroundImage =
                (themeEvent.theme.reactShellTheme && !themeEvent.theme.reactShellTheme.IsDarkMode
                    && (themeEvent.theme.themeType === 'App' as ThemeType || themeEvent.theme.themeType === 'Default' as ThemeType)
                    && !!this.contextService.sharedContext.fluentHeroImageUrl)
                && !this.capabilitiesService.capabilities.leftNavReact
                && !this.isConsumer
                && !(route && route.app)
                && route.route !== OfficeRoute.AppGallery;
        });
    }

    private flushLogs() {
        this.instrumentationService.flush();
    }

    private getSignOutLandingUrl(): string {
        let url = this.contextService.sharedContext.signOutRedirectUrl;
        if (!url) {
            return url;
        }

        // Append current query params to the signout url except for "auth=" parameter
        let currentQuery = this.windowService.nativeWindow.location.search;
        if (!currentQuery) {
            return url;
        }

        if (currentQuery.startsWith('?')) {
            currentQuery = currentQuery.substr(1);
        }

        let urlElements = url.split('#');
        const urlWithoutFragment = urlElements[0];
        const fragment = urlElements.length === 2 ? urlElements[1] : '';

        urlElements = urlWithoutFragment.split('?');
        const urlWithoutQuery = urlElements[0];
        const query = urlElements.length === 2 ? urlElements[1] : '';

        // Infuse query with existing parameters except for "auth=".
        let augmentedQuery = query;
        const currentQueryElements = currentQuery.split('&');
        for (const entry of currentQueryElements) {
            const paramName = entry.split('=')[0].toLowerCase();
            if (paramName !== 'auth' && paramName !== 'mockauth') {
                augmentedQuery += `${augmentedQuery ? '&' : ''}${entry}`;
            }
        }

        url = urlWithoutQuery;
        if (augmentedQuery) {
            url += `?${augmentedQuery}`;
        }
        if (fragment) {
            url += `#${fragment}`;
        }

        return url;
    }

    private prepareUpsellButton() {
        this.scheduler.scheduleWork(() => {
            import('./shared/utilities/upsell-button.utility').then(chunk => {
                this.upsellButtonUtility = new chunk.UpsellButton(this.injector.get(ConsumerInstallConfigService), this.contextService, this.eventService, this.injector, this.officeRouteService);
            });
        }, SchedulePriority.VeryLow);
    }
}
